import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Compass, Mail, Lock, User, ArrowRight, Gift, Shield, ChevronRight } from "lucide-react";
import { DisclaimerFull } from "@/components/Disclaimer";
import { CRISIS_RESOURCES } from "@/lib/crisisResources";

// Privacy Notice Version - update this when content changes
const PRIVACY_NOTICE_VERSION = "1.0";
const PRIVACY_NOTICE_DATE = "30/08/2026";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [privacyNoticeAccepted, setPrivacyNoticeAccepted] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Pending trial code from /trial redirect
  const [pendingTrialCode, setPendingTrialCode] = useState(null);
  
  // Check for companion app trial params
  const [trialParams, setTrialParams] = useState(null);
  
  useEffect(() => {
    // Check for PayFast/trial params
    const storedParams = sessionStorage.getItem('compass_trial_params');
    if (storedParams) {
      try {
        setTrialParams(JSON.parse(storedParams));
      } catch (e) {
        // Invalid JSON in sessionStorage - clear it and continue
        sessionStorage.removeItem('compass_trial_params');
        if (process.env.NODE_ENV === 'development') {
          console.debug('Invalid trial params in sessionStorage:', e.message);
        }
      }
    }
    
    // Check for pending trial code from /trial page
    const pendingCode = sessionStorage.getItem('compass_pending_trial_code');
    if (pendingCode) {
      setPendingTrialCode(pendingCode);
    }
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // If user hasn't completed onboarding, go there first
      if (!user.onboarding_complete) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/compass", { replace: true });
      }
    }
  }, [user, navigate]);

  if (user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Redirecting...</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!agreedToTerms) {
      toast.error("Please agree to the terms to continue");
      return;
    }

    if (!privacyNoticeAccepted) {
      toast.error("Please accept the privacy notice to continue");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      // Pass trial params and privacy acceptance if available
      await register(email, password, name, trialParams, privacyNoticeAccepted, PRIVACY_NOTICE_VERSION);
      
      // Clear stored params after successful registration
      sessionStorage.removeItem('compass_trial_params');
      
      // If there's a pending trial code, redeem it now
      if (pendingTrialCode) {
        try {
          const redeemResponse = await axios.post(
            `${API}/trial/redeem`,
            { code: pendingTrialCode },
            { withCredentials: true }
          );
          
          if (redeemResponse.data.success) {
            sessionStorage.removeItem('compass_pending_trial_code');
            toast.success(redeemResponse.data.message || "Trial activated! Full access unlocked.");
          }
        } catch (redeemError) {
          sessionStorage.removeItem('compass_pending_trial_code');
          const errorMsg = redeemError.response?.data?.detail || "Could not activate trial code.";
          if (errorMsg.includes("Invalid")) {
            toast.error("This trial code is invalid or expired.");
          } else if (errorMsg.includes("usage limit")) {
            toast.error("This trial code has reached its usage limit.");
          } else if (errorMsg.includes("no longer valid")) {
            toast.error("This trial link is no longer valid.");
          } else {
            toast.error(errorMsg);
          }
          // Still continue to onboarding - account was created successfully
        }
      } else if (trialParams) {
        toast.success(`Account created with ${trialParams.trial_days}-day full access trial!`);
      } else {
        toast.success("Account created! Let's set up your profile.");
      }
      navigate("/onboarding");
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/compass";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Visual */}
      <div className="hidden lg:flex flex-1 relative bg-secondary/20">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-center">
            <h2 className="text-4xl font-heading text-foreground mb-6">
              See your patterns.<br />Change your response.
            </h2>
            <p className="text-muted-foreground text-lg max-w-md">
              Daily signals, an AI companion, and tools for intentional living — free to start.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="/compass-logo.png" 
              alt="Compass" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-heading text-xl text-foreground">Compass</span>
          </Link>

          <div className="animate-fade-in">
            <h1 className="text-3xl md:text-4xl text-foreground mb-2">Begin your journey</h1>
            <p className="text-muted-foreground mb-8">Create your account to start the transformation.</p>

            {/* Trial code banner (from /trial link) */}
            {pendingTrialCode && !trialParams && (
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#a69373]/20 to-[#a69373]/10 border border-[#a69373]/30">
                <div className="flex items-center gap-3">
                  <Gift className="w-5 h-5 text-[#a69373]" />
                  <div>
                    <p className="text-white font-medium text-sm">Trial Code Applied</p>
                    <p className="text-white/60 text-xs">Your trial will activate after registration</p>
                  </div>
                </div>
              </div>
            )}

            {/* Trial banner for companion app referrals */}
            {trialParams && (
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#a69373]/20 to-[#a69373]/10 border border-[#a69373]/30">
                <div className="flex items-center gap-3">
                  <Gift className="w-5 h-5 text-[#a69373]" />
                  <div>
                    <p className="text-white font-medium text-sm">
                      {trialParams.trial_days}-Day Full Access Trial
                    </p>
                    <p className="text-white/60 text-xs">
                      {trialParams.event ? `From ${trialParams.event}` : 'All features unlocked'} — no payment required
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground">Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-field pl-12"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-12"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-12"
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-foreground">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field pl-12"
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              {/* Disclaimer */}
              <DisclaimerFull className="my-4" />

              {/* Agreement Checkbox */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#f0f5f0] border border-[#c5d5c5]">
                <Checkbox
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-0.5 border-[#7a9e7a] data-[state=checked]:bg-[#7a9e7a] data-[state=checked]:border-[#7a9e7a]"
                  data-testid="register-agree-checkbox"
                />
                <Label htmlFor="agree-terms" className="text-sm text-foreground/70 leading-relaxed cursor-pointer">
                  I understand that Compass is not a substitute for professional or medical advice and I take full responsibility for my use of this platform.
                </Label>
              </div>

              {/* Privacy Notice Checkbox - POPIA Compliance */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#f5f0e8] border border-[#a69373]/30">
                <Checkbox
                  id="privacy-notice"
                  checked={privacyNoticeAccepted}
                  onCheckedChange={setPrivacyNoticeAccepted}
                  className="mt-0.5 border-[#a69373] data-[state=checked]:bg-[#a69373] data-[state=checked]:border-[#a69373]"
                  data-testid="register-privacy-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="privacy-notice" className="text-sm text-foreground/70 leading-relaxed cursor-pointer">
                    I have read and accept the{" "}
                    <Dialog open={privacyDialogOpen} onOpenChange={setPrivacyDialogOpen}>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="text-[#a69373] hover:underline inline-flex items-center gap-1 font-medium"
                          data-testid="privacy-notice-link"
                        >
                          Privacy Notice
                          <Shield className="w-3 h-3" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2 text-[#5a8a5a]">
                            <Shield className="w-5 h-5" />
                            PRIVACY NOTICE
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 text-sm text-foreground/80">
                          <p className="font-medium">
                            Compass by TrueNorth Zone<br />
                            Version {PRIVACY_NOTICE_VERSION}
                          </p>
                          
                          <p>
                            ZenZone (Pty) Ltd, registration number 2023/782263/07, trading as
                            TrueNorth Zone ("we") operates Compass. This notice explains what we
                            collect, where it goes, and what you can do about it. It is written to
                            meet the Protection of Personal Information Act, 2013 (POPIA).
                          </p>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">WHO IS RESPONSIBLE</h3>
                            <p>
                              ZenZone (Pty) Ltd, registration number 2023/782263/07, trading as
                              TrueNorth Zone.<br />
                              Contact: info@truenorth-zone.com
                            </p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">WHAT WE COLLECT</h3>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Your name and email address, when you register.</li>
                              <li>What you write: journal entries, gratitude entries, check-in notes, and messages you send to PathFinder.</li>
                              <li>What you record: mood and energy scores, daily check-ins, streaks, quest and course progress.</li>
                              <li>Payment information, if you subscribe. Card details are handled by PayFast and never reach us.</li>
                            </ul>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">WHY WE COLLECT IT</h3>
                            <p>
                              To run the app: to show you your own history and patterns, to generate
                              PathFinder responses and weekly reflections, to send you reminders if
                              you ask for them, and to process payment if you subscribe. We do not
                              sell your information, and we do not use it for advertising.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">WHERE YOUR INFORMATION GOES</h3>
                            <p>
                              Compass runs on Emergent, a hosting platform. Your data, including
                              everything you write, is stored and processed on Emergent's
                              infrastructure in the United States and India. It therefore leaves South
                              Africa.
                            </p>
                            <p>
                              PathFinder and the weekly reflections are generated by Anthropic's
                              Claude. When you use PathFinder, the following is sent to Anthropic
                              through Emergent: your message, recent journal excerpts, your mood and
                              energy scores, your streak, and your recent PathFinder messages. This
                              content is subject to Emergent's and Anthropic's terms. We are not able
                              to guarantee how those parties retain or use it beyond what their
                              published terms state.
                            </p>
                            <p>
                              Emails are sent through Resend, which receives your name and email
                              address. Payments are processed by PayFast, which receives your name,
                              email address and payment amount.
                            </p>
                            <p>
                              By registering, you consent to your personal information being
                              transferred to and processed in these countries, as required by section
                              72 of POPIA.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">HOW LONG WE KEEP IT</h3>
                            <p>
                              For as long as your account exists.<br />
                              If you delete your account, your data is removed from the live database
                              immediately. Copies persist in Emergent's automated backups and expire
                              within twelve months. We cannot delete data from those backups before
                              they expire.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">SECURITY</h3>
                            <p>
                              Passwords are hashed and never stored in readable form. Connections are
                              encrypted. Emergent encrypts data at rest and restricts staff access.
                              No system is perfectly secure, and we do not claim otherwise.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">YOUR RIGHTS UNDER POPIA</h3>
                            <p>You may:</p>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>see what we hold about you. Use Export My Data in your profile to download everything, at any time, without asking us.</li>
                              <li>correct anything inaccurate.</li>
                              <li>delete your account and all its data. Use Delete My Account in your profile.</li>
                              <li>withdraw this consent, by deleting your account.</li>
                              <li>object to how we process your information.</li>
                              <li>complain to the Information Regulator of South Africa: inforeg@justice.gov.za</li>
                            </ul>
                            <p>To exercise any of these, contact info@truenorth-zone.com.</p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">WHAT COMPASS IS NOT</h3>
                            <p>
                              Compass is a self-development tool. It is not therapy, medical care, or
                              a substitute for professional help. Nothing in it is clinical advice. If
                              you are struggling, please speak to a qualified professional. In South
                              Africa, the SADAG Suicide Crisis Helpline is {CRISIS_RESOURCES.phone}, available 24
                              hours, or SMS {CRISIS_RESOURCES.sms} for a call-back. For a referral to a psychologist or
                              psychiatrist, the SADAG Mental Health Line is {CRISIS_RESOURCES.mentalHealthLine}, open 8am to
                              8pm daily.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">CHANGES</h3>
                            <p>
                              If this notice changes materially, we will ask you to accept the new
                              version.<br />
                              Version {PRIVACY_NOTICE_VERSION}, {PRIVACY_NOTICE_DATE}
                            </p>
                          </div>
                        </div>

                        <Button 
                          onClick={() => {
                            setPrivacyNoticeAccepted(true);
                            setPrivacyDialogOpen(false);
                          }}
                          className="w-full mt-4 bg-[#5a8a5a] hover:bg-[#4a7a4a]"
                          data-testid="accept-privacy-btn"
                        >
                          I Accept
                        </Button>
                      </DialogContent>
                    </Dialog>
                    {" "}and consent to the processing of my personal information.
                  </Label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full py-6"
                disabled={loading || !agreedToTerms || !privacyNoticeAccepted}
                data-testid="register-submit-btn"
              >
                {loading ? "Creating account..." : "Create Account"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-background text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="btn-secondary w-full py-6"
              onClick={handleGoogleRegister}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            <p className="mt-8 text-center text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
